<script lang="ts">
import { TextButton } from '@goodlifeplus/component-library'
import { type PropType, defineComponent } from 'vue'
import { type RouteLocationRaw, RouterLink } from 'vue-router'

type HTMLElementTagName = keyof HTMLElementTagNameMap

/**
 * An enhanced wrapper around `<TextButton>` that adds support for routing,
 * hyperlinks, and loading state handling.
 *
 * This component extends `TextButton` and dynamically determines which
 * underlying element or component to render based on the provided props:
 * - Renders a `<RouterLink>` when `route` is provided (via `to`)
 * - Renders an `<a>` element when `href` is provided
 * - Falls back to a native `<button>` otherwise
 *
 * It automatically wires up the `as` prop and forwards all relevant props
 * and attributes (`$attrs`) to the underlying component, while overriding
 * where necessary (e.g. `to`, `href`, `as`).
 *
 * Additional features:
 * - `isLoading`: when true, the button can display a loading state
 *   (e.g. swapping its content for a spinner)
 *
 * This abstraction allows consumers to use a single API for buttons,
 * links, and router navigation without needing to manage the underlying
 * implementation details.
 */
export default defineComponent({
  components: {
    TextButton,
  },
  extends: TextButton,
  props: {
    /**
     * If set, the button will render as a `<RouterLink>`
     */
    route: {
      type: [String, Object] as PropType<RouteLocationRaw>,
    },
    /**
     * If set, the button will render as a `<a>`
     */
    href: {
      type: String,
    },
  },
  computed: {
    /**
     * Computes which props and attributes should be forwarded on to the
     * wrapped component. This should remove *all* props from this wrapper
     * and only pass on what's needed to prevent invalid values making it into
     * the DOM as HTML attributes.
     */
    forwardedProps() {
      const { route, href, ...rest } = this.$props
      if (route) {
        return {
          ...rest,
          ...this.$attrs,
          to: route,
          as: RouterLink,
        }
      } else if (href) {
        return {
          ...rest,
          ...this.$attrs,
          href,
          as: 'a' as HTMLElementTagName,
        }
      }
      return {
        ...rest,
        ...this.$attrs,
      }
    },
  },
})
</script>

<template>
  <TextButton v-bind="forwardedProps" />
</template>
